import { useEffect, useState } from 'react';
import type { ResendLimiterState, UseResendLimiterOptions } from '../types';

/**
 * Custom hook to manage resend attempt limits with time restrictions
 * TODO: we really should handle this in the backend
 */
function useResendLimiter(options?: UseResendLimiterOptions): ResendLimiterState {
  const { maxAttempts = 5, resetTime = 60 * 60 * 1000, storageKey = 'otpResendLimiter' } = options || {};

  const [attempts, setAttempts] = useState(0);
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);

  // Check on mount and then periodically
  useEffect(() => {
    // Check for attempts limit
    const checkAttemptsLimit = () => {
      const storedData = localStorage.getItem(storageKey);
      if (!storedData) {
        setAttempts(0);
        setMaxAttemptsReached(false);
        return;
      }

      try {
        const { attempts, timestamp } = JSON.parse(storedData);
        const timeElapsed = Date.now() - timestamp;
        const isWithinResetTime = timeElapsed < resetTime;

        if (isWithinResetTime) {
          setAttempts(attempts);
          setMaxAttemptsReached(attempts >= maxAttempts);
        } else {
          // Reset if time has passed
          localStorage.removeItem(storageKey);
          setAttempts(0);
          setMaxAttemptsReached(false);
        }
      } catch {
        // If there's an error parsing the data, reset it
        localStorage.removeItem(storageKey);
        setAttempts(0);
        setMaxAttemptsReached(false);
      }
    };

    // Run immediately
    checkAttemptsLimit();

    // Set up interval to periodically check
    const intervalId = setInterval(checkAttemptsLimit, 1000);

    return () => clearInterval(intervalId);
  }, [maxAttempts, resetTime, storageKey]);

  // Increment attempts
  const incrementAttempts = () => {
    const storedData = localStorage.getItem(storageKey);
    let currentAttempts = 0;

    if (storedData) {
      try {
        const { attempts } = JSON.parse(storedData);
        currentAttempts = attempts;
      } catch {
        // If error, start from 0
      }
    }

    const newAttempts = currentAttempts + 1;
    setAttempts(newAttempts);

    // Store in localStorage
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        attempts: newAttempts,
        timestamp: Date.now(),
      })
    );

    if (newAttempts >= maxAttempts) {
      setMaxAttemptsReached(true);
    }
  };

  // Manual reset
  const resetAttempts = () => {
    localStorage.removeItem(storageKey);
    setAttempts(0);
    setMaxAttemptsReached(false);
  };

  return {
    attempts,
    maxAttemptsReached,
    incrementAttempts,
    resetAttempts,
  };
}

export { useResendLimiter };
